import { useState } from 'react';
import ReportsNav from '../../components/ReportsNav';
import { useQuery } from '@tanstack/react-query';
import { invoicesApi } from '../../api/invoices';
import { formatMoney } from '../../utils/formatMoney';
import { monthsAgo, today } from '../../utils/formatDate';
import LoadingSpinner from '../../components/LoadingSpinner';
import DatePickerInput from '../../components/DatePickerInput';

interface VatBucket {
  rate: number;
  rateLabel: string;
  salesNet: number;
  salesVat: number;
  purchaseNet: number;
  purchaseVat: number;
}

export default function VatSummary() {
  const [from, setFrom] = useState(monthsAgo(1));
  const [to, setTo] = useState(today());

  const { data: salesInvoices, isLoading: loadingSales } = useQuery({
    queryKey: ['invoices-made', from, to],
    queryFn: () => invoicesApi.list({ direction: 'Made', from, to, limit: 1000 }),
  });

  const { data: purchaseInvoices, isLoading: loadingPurchases } = useQuery({
    queryKey: ['invoices-received', from, to],
    queryFn: () => invoicesApi.list({ direction: 'Received', from, to, limit: 1000 }),
  });

  if (loadingSales || loadingPurchases) return <LoadingSpinner />;

  // Bucket VAT by rate
  const buckets = new Map<string, VatBucket>();

  const addInvoiceToBucket = (invoices: typeof salesInvoices, isSales: boolean) => {
    for (const inv of invoices ?? []) {
      for (const line of inv.lines) {
        const rateKey = line.vatRate.toFixed(4);
        if (!buckets.has(rateKey)) {
          buckets.set(rateKey, {
            rate: line.vatRate,
            rateLabel: `${(line.vatRate * 100).toFixed(0)}%`,
            salesNet: 0,
            salesVat: 0,
            purchaseNet: 0,
            purchaseVat: 0,
          });
        }
        const bucket = buckets.get(rateKey)!;
        if (isSales) {
          bucket.salesNet += line.sum;
          bucket.salesVat += line.sumVat;
        } else {
          bucket.purchaseNet += line.sum;
          bucket.purchaseVat += line.sumVat;
        }
      }
    }
  };

  addInvoiceToBucket(salesInvoices, true);
  addInvoiceToBucket(purchaseInvoices, false);

  const sortedBuckets = Array.from(buckets.values()).sort((a, b) => b.rate - a.rate);

  const totalSalesNet = sortedBuckets.reduce((a, b) => a + b.salesNet, 0);
  const totalSalesVat = sortedBuckets.reduce((a, b) => a + b.salesVat, 0);
  const totalPurchaseNet = sortedBuckets.reduce((a, b) => a + b.purchaseNet, 0);
  const totalPurchaseVat = sortedBuckets.reduce((a, b) => a + b.purchaseVat, 0);
  const netPayable = totalSalesVat - totalPurchaseVat;

  return (
    <div className="space-y-4">
<ReportsNav />

      {/* Date range filter */}
      <div className="flex gap-3 items-end">
        <DatePickerInput
          label="Nuo"
          value={from}
          onChange={setFrom}
          inputClassName="w-36"
        />
        <DatePickerInput
          label="Iki"
          value={to}
          onChange={setTo}
          inputClassName="w-36"
        />
      </div>

      {/* VAT table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="dense w-full">
          <thead>
            <tr>
              <th className="text-left">PVM tarifas</th>
              <th className="text-right">Pardavimai be PVM</th>
              <th className="text-right">Pardavimų PVM</th>
              <th className="text-right">Pardavimai viso</th>
              <th className="text-right">Pirkimai be PVM</th>
              <th className="text-right">Pirkimų PVM</th>
              <th className="text-right">Pirkimai viso</th>
            </tr>
          </thead>
          <tbody>
            {sortedBuckets.map((bucket) => (
              <tr key={bucket.rateLabel}>
                <td className="font-medium">{bucket.rateLabel}</td>
                <td className="num">{formatMoney(bucket.salesNet)}</td>
                <td className="num">{formatMoney(bucket.salesVat)}</td>
                <td className="num">{formatMoney(bucket.salesNet + bucket.salesVat)}</td>
                <td className="num">{formatMoney(bucket.purchaseNet)}</td>
                <td className="num">{formatMoney(bucket.purchaseVat)}</td>
                <td className="num">{formatMoney(bucket.purchaseNet + bucket.purchaseVat)}</td>
              </tr>
            ))}
            {sortedBuckets.length === 0 && (
              <tr><td colSpan={7} className="text-center text-gray-400 py-4">Pasirinktu laikotarpiu sąskaitų nėra</td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 font-semibold">
              <td className="text-right">Viso</td>
              <td className="num">{formatMoney(totalSalesNet)}</td>
              <td className="num">{formatMoney(totalSalesVat)}</td>
              <td className="num">{formatMoney(totalSalesNet + totalSalesVat)}</td>
              <td className="num">{formatMoney(totalPurchaseNet)}</td>
              <td className="num">{formatMoney(totalPurchaseVat)}</td>
              <td className="num">{formatMoney(totalPurchaseNet + totalPurchaseVat)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Net payable summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <div className="text-xs text-gray-500">Pardavimų PVM (mokėtina)</div>
          <div className="text-xl font-bold num text-primary-700">{formatMoney(totalSalesVat)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <div className="text-xs text-gray-500">Pirkimų PVM (atskaitoma)</div>
          <div className="text-xl font-bold num text-orange-700">{formatMoney(totalPurchaseVat)}</div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4 text-center">
          <div className="text-xs text-gray-500">Mokėtina VMI iš viso</div>
          <div className={`text-xl font-bold num ${netPayable >= 0 ? 'text-red-700' : 'text-green-700'}`}>
            {formatMoney(netPayable)}
          </div>
        </div>
      </div>
    </div>
  );
}
